import type * as React from 'react';
import { controlClasses } from '../lib/control-classes.js';
import { useFieldA11y } from '../lib/field-context.js';
import { useFieldError } from '../lib/use-field-error.js';
import { useRegistration } from '../lib/use-registration.js';

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  prefixIcon?: React.ReactNode;
  prefix?: string;
  suffix?: string;
}

export function TextField({
  name,
  prefixIcon,
  prefix,
  suffix,
  className,
  onChange,
  onBlur,
  ...props
}: TextFieldProps) {
  const hasError = !!useFieldError(name);
  const a11y = useFieldA11y(name);
  const bind = useRegistration<HTMLInputElement>(name, { onChange, onBlur });

  const inputClasses = controlClasses({
    hasError,
    hasPrefixIcon: !!prefixIcon,
    hasPrefix: !!prefix,
    hasSuffix: !!suffix,
    className,
  });

  const input = <input {...a11y} className={inputClasses} {...props} {...bind} />;

  if (!prefixIcon && !prefix && !suffix) return input;

  return (
    <div className="relative">
      {prefixIcon && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2 text-muted-foreground [&>svg]:size-full"
        >
          {prefixIcon}
        </span>
      )}
      {prefix && (
        <span className="absolute inset-y-0 left-0 flex items-center border-r border-input px-2.5 text-[12px] font-medium text-muted-foreground">
          {prefix}
        </span>
      )}
      {input}
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}
